import React from 'react';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';

/**
 * Standard button atom — see 0_web_labelflow/DESIGN_GUIDE.md.
 *
 * Preferred usage (new code):
 *   <Button variant="primary" size="sm" onClick={...}>Save</Button>
 * Variants: primary | secondary | success | danger | ghost |
 *           outline-primary | outline-secondary | outline-success | outline-danger
 *
 * Legacy usage (still supported): className="btn btn-secondary btn-sm".
 * `variant`/`size` compose with className, so migration can be gradual.
 */
const VARIANT_CLASS = {
  primary: 'btn btn-primary',
  secondary: 'btn btn-secondary',
  success: 'btn btn-success',
  danger: 'btn btn-danger',
  ghost: 'btn btn-ghost',
  'outline-primary': 'btn btn-outline-primary',
  'outline-secondary': 'btn btn-outline-secondary',
  'outline-success': 'btn btn-outline-success',
  'outline-danger': 'btn btn-outline-danger',
};

const SIZE_CLASS = { sm: 'btn-sm', lg: 'btn-lg' };

const Button = ({
  type = 'button',
  onClick,
  children,
  className = '',
  tooltipText = undefined,
  disabled = false,
  variant = undefined,
  size = undefined,
  active = false,
}) => {
  const classes = [
    variant ? (VARIANT_CLASS[variant] || `btn btn-${variant}`) : '',
    size ? SIZE_CLASS[size] || '' : '',
    active ? 'active' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * `animateFill={false}` is load-bearing, not a style preference.
   *
   * With react-tippy's default (`animateFill: true`) the library stamps
   * `data-animatefill` on the tooltip, and its own rule
   * `.tippy-tooltip[data-animatefill]{background-color:transparent}` then wins —
   * the visible fill becomes a separate `[x-circle]` element hardcoded to #333.
   * So our `background-color: var(--popover)` never painted while the text
   * colour did, and in LIGHT mode that left #101418 on #333: a contrast ratio
   * of 1.46:1 against a WCAG AA floor of 4.5, i.e. unreadable. Dark mode got
   * away with it at 10.8:1, which is why it survived.
   *
   * See the Tippy block in src/styles/theme-overrides.css.
   */
  return (
    <Tooltip
      title={tooltipText ? tooltipText : undefined}
      trigger="mouseenter"
      animateFill={false}
    >
      <button
        type={type}
        onClick={onClick ? onClick : undefined} // only add onClick if passed
        className={classes}
        disabled={disabled}
      >
        {children}
      </button>
    </Tooltip>
  );
};

export default Button;
